import { useId } from "react";

import { Button } from "../../../../../core/Button.js";
import { cardStyles } from "../../../../../core/Card.styles.js";
import { DescriptionList } from "../../../../../core/DescriptionList.js";
import { Table, type TableColumn } from "../../../../../core/Table.js";
import { Text } from "../../../../../core/Text.js";
import { cn } from "../../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../../foundations/interaction.js";
import type { BattleFairnessContent, BattleFairnessRow } from "../../../battlesContent.js";

const MONO = "font-mono text-mono-meta";

/**
 * The battle's fairness inputs behind Show: the salt, the day's seed (hashed until its day ends),
 * and every revealed open's client seed, nonce and result, so any of them can be recomputed.
 */
export function BattleFairness({
  title,
  description,
  toggle,
  facts,
  opens,
  verification,
}: BattleFairnessContent) {
  const id = useId();
  const columns: TableColumn<BattleFairnessRow>[] = [
    { key: "round", header: opens.headers.round, cell: (row) => row.round, rowHeader: true },
    { key: "seat", header: opens.headers.seat, cell: (row) => row.seat },
    {
      key: "clientSeed",
      header: opens.headers.clientSeed,
      cell: (row) => <span className={MONO}>{row.clientSeed}</span>,
    },
    {
      key: "nonce",
      header: opens.headers.nonce,
      cell: (row) => <span className={MONO}>{row.nonce}</span>,
      numeric: true,
    },
    {
      key: "result",
      header: opens.headers.result,
      cell: (row) => <span className={MONO}>{row.result}</span>,
      numeric: true,
    },
    { key: "item", header: opens.headers.item, cell: (row) => row.item },
  ];
  return (
    <section
      aria-labelledby={id}
      className={cardStyles({ radius: "2xl", className: "flex min-w-0 flex-col gap-4 p-5" })}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Text as="h2" id={id} variant="heading-h3" tone="primary">
            {title}
          </Text>
          <Text as="p" variant="body-b3" tone="tertiary">
            {description}
          </Text>
        </div>
        <Button variant="secondary" size="sm" aria-expanded={toggle.open} onClick={toggle.onToggle}>
          {toggle.open ? toggle.hideLabel : toggle.showLabel}
        </Button>
      </div>
      {toggle.open && (
        <>
          <DescriptionList items={facts.map((fact) => ({ ...fact, mono: true }))} />
          {opens.rows.length > 0 ? (
            <Table
              caption={opens.caption}
              hideCaption
              columns={columns}
              rows={opens.rows}
              rowKey={(row) => row.key}
              density="compact"
            />
          ) : (
            <Text as="p" variant="body-b3" tone="tertiary">
              {opens.emptyLabel}
            </Text>
          )}
          {verification && (
            <a
              href={verification.href}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "self-start rounded-xs font-body text-body-b3 text-accent-text underline",
                FOCUS_RING,
              )}
            >
              {verification.label}
            </a>
          )}
        </>
      )}
    </section>
  );
}
